/**
 * Merchant Earnings Dashboard
 * Sales, fees, payouts for business merchants
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import api from '../lib/api';
import {
  ArrowLeft,
  DollarSign,
  TrendingUp,
  ShoppingBag,
  Package,
  Calendar,
  ChevronRight,
  Wallet,
  ArrowUpRight,
  Loader2,
  XCircle,
  Percent,
  CreditCard
} from 'lucide-react';

const COLORS = {
  green: '#00A86B',
  yellow: '#FFD300',
  black: '#000000',
};

const MerchantEarnings = () => {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [orders, setOrders] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('month');
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawMethod, setWithdrawMethod] = useState('bank_transfer');
  const [withdrawing, setWithdrawing] = useState(false);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [summaryRes, ordersRes, withdrawalsRes] = await Promise.all([
        api.get('/finance/merchant/earnings/summary'),
        api.get(`/finance/merchant/orders?period=${activeTab}`),
        api.get('/finance/merchant/withdrawals?limit=5')
      ]);
      
      setSummary(summaryRes.data);
      setOrders(ordersRes.data.orders || []);
      setWithdrawals(withdrawalsRes.data.withdrawals || []);
    } catch (error) {
      console.error('Failed to load merchant data');
    } finally {
      setLoading(false);
    }
  };

  const handleWithdraw = async (e) => {
    e.preventDefault();
    const amount = parseFloat(withdrawAmount);
    
    if (!amount || amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    
    if (amount > (summary?.pending_payout_balance || 0)) {
      toast.error('Insufficient balance');
      return;
    }
    
    setWithdrawing(true);
    try {
      await api.post('/finance/merchant/withdrawals', {
        amount,
        method: withdrawMethod
      });
      toast.success('Payout request submitted!');
      setShowWithdrawModal(false);
      setWithdrawAmount('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Payout request failed');
    } finally {
      setWithdrawing(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'text-green-600 bg-green-100';
      case 'approved': return 'text-blue-600 bg-blue-100';
      case 'pending': return 'text-amber-600 bg-amber-100';
      case 'rejected': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  if (loading && !summary) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <Loader2 className="h-8 w-8 animate-spin text-[#00A86B]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-6 bg-gray-50">
      {/* Header */}
      <header className="bg-white px-5 py-4 shadow-sm flex items-center gap-4">
        <Button
          variant="ghost"
          onClick={() => navigate('/business')}
          className="p-2 hover:bg-gray-100 rounded-full"
        >
          <ArrowLeft className="h-5 w-5 text-gray-600" />
        </Button>
        <div>
          <h1 className="text-lg font-bold text-gray-800">Merchant Earnings</h1>
          <p className="text-xs text-gray-500">Sales & Payouts</p>
        </div>
      </header>

      <main className="p-4 space-y-4">
        {/* Summary Card */}
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-[#00A86B]/20">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-sm text-gray-500 mb-1">Pending Payout Balance</p>
              <h2 className="text-3xl font-bold text-[#00A86B]">
                J${summary?.pending_payout_balance?.toLocaleString() || '0'}
              </h2>
            </div>
            <Button
              onClick={() => setShowWithdrawModal(true)}
              className="rounded-xl text-white font-medium"
              style={{ background: COLORS.green }}
            >
              <Wallet className="h-4 w-4 mr-2" />
              Request Payout
            </Button>
          </div>
          
          <div className="grid grid-cols-4 gap-3 pt-4 border-t border-gray-100">
            <div className="text-center">
              <p className="text-lg font-bold text-gray-800">J${summary?.today_sales?.toLocaleString() || '0'}</p>
              <p className="text-xs text-gray-500">Today</p>
            </div>
            <div className="text-center">
              <p className="text-lg font-bold text-gray-800">{summary?.today_orders || 0}</p>
              <p className="text-xs text-gray-500">Orders</p>
            </div>
            <div className="text-center">
              <p className="text-lg font-bold text-gray-800">J${summary?.month_sales?.toLocaleString() || '0'}</p>
              <p className="text-xs text-gray-500">This Month</p>
            </div>
            <div className="text-center">
              <p className="text-lg font-bold text-gray-800">{summary?.month_orders || 0}</p>
              <p className="text-xs text-gray-500">Orders</p>
            </div>
          </div>
        </div>

        {/* Period Tabs */}
        <div className="flex gap-2 bg-white rounded-xl p-1 shadow-sm">
          {['today', 'week', 'month', 'all'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${
                activeTab === tab
                  ? 'bg-[#00A86B] text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {/* Orders & Fees Table */}
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          <div className="p-4 border-b border-gray-100">
            <h3 className="font-bold text-gray-800 flex items-center gap-2">
              <ShoppingBag className="h-5 w-5 text-[#00A86B]" />
              Orders & Fees
            </h3>
          </div>
          
          {orders.length === 0 ? (
            <div className="p-6 text-center text-gray-500">
              <Package className="h-10 w-10 mx-auto mb-2 opacity-30" />
              <p>No orders for this period</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Order</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">Total</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">Fee (9.45%)</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">Net</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {orders.map((order) => (
                    <tr key={order.order_id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <p className="font-medium text-gray-800">#{order.order_id.slice(0, 8)}</p>
                        <p className="text-xs text-gray-500">
                          {new Date(order.order_date).toLocaleDateString()}
                        </p>
                      </td>
                      <td className="px-4 py-3 text-right font-medium">
                        J${order.order_total.toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-right text-red-500">
                        -J${order.platform_fee.toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-right font-bold text-[#00A86B]">
                        J${order.net_amount.toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(order.payout_status)}`}>
                          {order.payout_status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Fee Breakdown */}
        <div className="bg-white rounded-2xl p-4 shadow-sm">
          <h3 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
            <Percent className="h-5 w-5 text-[#00A86B]" />
            Fee Structure
          </h3>
          <div className="space-y-2">
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
              <span className="text-gray-600">Seller Fee</span>
              <span className="font-bold text-gray-800">9.45%</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
              <span className="text-gray-600">Transfer Fee</span>
              <span className="font-bold text-gray-800">J$0</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-[#00A86B]/10 rounded-xl">
              <span className="text-gray-600">You Receive</span>
              <span className="font-bold text-[#00A86B]">90.55% of sales</span>
            </div>
          </div>
        </div>

        {/* Payout History */}
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          <div className="p-4 border-b border-gray-100">
            <h3 className="font-bold text-gray-800 flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-[#00A86B]" />
              Payout History
            </h3>
          </div>
          
          {withdrawals.length === 0 ? (
            <div className="p-6 text-center text-gray-500">
              <Wallet className="h-10 w-10 mx-auto mb-2 opacity-30" />
              <p>No payouts yet</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {withdrawals.map((w) => (
                <div key={w.id} className="p-4 flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-800">J${w.amount.toLocaleString()}</p>
                    <p className="text-xs text-gray-500">
                      {new Date(w.created_at).toLocaleDateString()} • {w.method}
                    </p>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(w.status)}`}>
                    {w.status}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Lifetime Stats */}
        <div className="bg-white rounded-2xl p-4 shadow-sm">
          <h3 className="font-bold text-gray-800 mb-3">Lifetime Summary</h3>
          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 bg-gray-50 rounded-xl">
              <p className="text-xl font-bold text-gray-800">J${summary?.lifetime_sales?.toLocaleString() || '0'}</p>
              <p className="text-xs text-gray-500">Total Sales</p>
            </div>
            <div className="p-3 bg-gray-50 rounded-xl">
              <p className="text-xl font-bold text-red-500">J${summary?.lifetime_fees?.toLocaleString() || '0'}</p>
              <p className="text-xs text-gray-500">Total Fees</p>
            </div>
            <div className="p-3 bg-[#00A86B]/10 rounded-xl">
              <p className="text-xl font-bold text-[#00A86B]">J${summary?.lifetime_payouts?.toLocaleString() || '0'}</p>
              <p className="text-xs text-gray-500">Total Received</p>
            </div>
            <div className="p-3 bg-gray-50 rounded-xl">
              <p className="text-xl font-bold text-gray-800">
                {summary?.last_payout_at 
                  ? new Date(summary.last_payout_at).toLocaleDateString()
                  : 'Never'}
              </p>
              <p className="text-xs text-gray-500">Last Payout</p>
            </div>
          </div>
        </div>
      </main>

      {/* Withdrawal Modal */}
      {showWithdrawModal && (
        <div className="fixed inset-0 bg-black/50 flex items-end justify-center z-50">
          <div className="bg-white w-full max-w-md rounded-t-3xl p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold text-gray-800">Request Payout</h3>
              <button onClick={() => setShowWithdrawModal(false)} className="text-gray-400 hover:text-gray-600">
                <XCircle className="h-6 w-6" />
              </button>
            </div>
            
            <p className="text-sm text-gray-500 mb-4">
              Available: <span className="font-bold text-[#00A86B]">J${summary?.pending_payout_balance?.toLocaleString() || '0'}</span>
            </p>
            
            <form onSubmit={handleWithdraw}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Amount (JMD)</label>
                <input
                  type="number"
                  value={withdrawAmount}
                  onChange={(e) => setWithdrawAmount(e.target.value)}
                  placeholder="Enter amount"
                  min="500"
                  max={summary?.pending_payout_balance || 0}
                  style={{color: "#111827", backgroundColor: "white"}} className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:border-[#00A86B]"
                />
                <p className="text-xs text-gray-400 mt-1">Minimum: J$500</p>
              </div>
              
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Payout Method</label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { id: 'bank_transfer', label: 'Bank' },
                    { id: 'stripe', label: 'Stripe' },
                    { id: 'fygaro', label: 'Fygaro' }
                  ].map((method) => (
                    <button
                      key={method.id}
                      type="button"
                      onClick={() => setWithdrawMethod(method.id)}
                      className={`py-2 rounded-xl text-sm font-medium transition-all ${
                        withdrawMethod === method.id
                          ? 'bg-[#00A86B] text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {method.label}
                    </button>
                  ))}
                </div>
              </div>
              
              <Button
                type="submit"
                disabled={withdrawing}
                className="w-full h-12 rounded-xl text-white font-bold"
                style={{ background: COLORS.green }}
              >
                {withdrawing ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  'Submit Request'
                )}
              </Button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default MerchantEarnings;
